import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { trackEvent, EVENTS } from '@/lib/analytics';

const CartContext = createContext(null);

const STORAGE_KEY = 'voltide_quote_cart';

function loadCart() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : { items: [], sites: [{ id: 'site-1', name: 'Site 1', address: '', city: '', contact: '', uploads: [] }] };
  } catch { return { items: [], sites: [{ id: 'site-1', name: 'Site 1', address: '', city: '', contact: '', uploads: [] }] }; }
}

function saveCart(cart) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(cart));
}

export function CartProvider({ children }) {
  const [cart, setCart] = useState(loadCart);

  useEffect(() => { saveCart(cart); }, [cart]);

  const addItem = useCallback((product, mode = 'rent', quantity = 1, siteId = 'site-1') => {
    setCart(prev => {
      const existing = prev.items.find(i => i.sku === product.sku && i.siteId === siteId);
      if (existing) {
        return { ...prev, items: prev.items.map(i =>
          i.sku === product.sku && i.siteId === siteId ? { ...i, quantity: i.quantity + quantity } : i
        )};
      }
      return { ...prev, items: [...prev.items, {
        sku: product.sku,
        product_name: product.product_name,
        pcs_kw: product.pcs_kw,
        battery_kwh: product.battery_kwh,
        cooling: product.cooling,
        image: product.image,
        mode,
        quantity,
        siteId,
        checkout_eligible: product.checkout_eligible || false,
        install_complexity: product.install_complexity || 'STANDARD',
        buy_from_price_ex_gst: product.buy_from_price_ex_gst || 0,
        rent_from_inr_per_month: product.rent_from_inr_per_month || 0,
      }]};
    });
    trackEvent(EVENTS.ADD_TO_QUOTE_CART, { sku: product.sku, mode });
  }, []);

  const removeItem = useCallback((sku, siteId) => {
    setCart(prev => ({ ...prev, items: prev.items.filter(i => !(i.sku === sku && i.siteId === siteId)) }));
  }, []);

  const updateItemMode = useCallback((sku, siteId, mode) => {
    setCart(prev => ({ ...prev, items: prev.items.map(i =>
      i.sku === sku && i.siteId === siteId ? { ...i, mode } : i
    )}));
  }, []);

  const updateItemQuantity = useCallback((sku, siteId, quantity) => {
    if (quantity <= 0) return removeItem(sku, siteId);
    setCart(prev => ({ ...prev, items: prev.items.map(i =>
      i.sku === sku && i.siteId === siteId ? { ...i, quantity } : i
    )}));
  }, [removeItem]);

  const addSite = useCallback(() => {
    const id = `site-${Date.now()}`;
    setCart(prev => ({ ...prev, sites: [...prev.sites, { id, name: `Site ${prev.sites.length + 1}`, address: '', city: '', contact: '', uploads: [] }] }));
  }, []);

  const updateSite = useCallback((siteId, data) => {
    setCart(prev => ({ ...prev, sites: prev.sites.map(s => s.id === siteId ? { ...s, ...data } : s) }));
  }, []);

  const clearCart = useCallback(() => {
    setCart({ items: [], sites: [{ id: 'site-1', name: 'Site 1', address: '', city: '', contact: '', uploads: [] }] });
  }, []);

  const copySiteItems = useCallback((fromSiteId, toSiteId) => {
    setCart(prev => {
      const itemsToCopy = prev.items.filter(i => i.siteId === fromSiteId).map(i => ({ ...i, siteId: toSiteId }));
      const newItems = [...prev.items];
      itemsToCopy.forEach(copyItem => {
        const existing = newItems.find(i => i.sku === copyItem.sku && i.siteId === toSiteId);
        if (existing) {
          existing.quantity += copyItem.quantity;
        } else {
          newItems.push(copyItem);
        }
      });
      return { ...prev, items: newItems };
    });
  }, []);

  const itemCount = cart.items.reduce((sum, i) => sum + i.quantity, 0);
  const allBuyEligible = cart.items.length > 0 && cart.items.every(i => i.mode === 'buy' && i.checkout_eligible && i.install_complexity === 'SIMPLE');
  const hasRentItems = cart.items.some(i => i.mode === 'rent');

  return (
    <CartContext.Provider value={{ cart, addItem, removeItem, updateItemMode, updateItemQuantity, addSite, updateSite, clearCart, copySiteItems, itemCount, allBuyEligible, hasRentItems }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
